/**
 * S8: 成果广场
 * 部署路径: /app/apps/main-site/frontend/src/pages/ShowcasePage.jsx
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HeartIcon, BookmarkIcon, ChatBubbleLeftEllipsisIcon,
  EyeIcon, XMarkIcon, PaperAirplaneIcon, StarIcon
} from '@heroicons/react/24/outline';
import {
  HeartIcon as HeartSolid,
  BookmarkIcon as BookmarkSolid,
  StarIcon as StarSolid
} from '@heroicons/react/24/solid';
import api from '../services/api';

// ─── 留言区组件 ───────────────────────────────────────────────────────────────
function CommentSection({ itemId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const isLoggedIn = !!localStorage.getItem('user_token');

  useEffect(() => {
    api.get(`/showcase/items/${itemId}/comments`)
      .then(r => setComments(r.data.comments || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [itemId]);

  const handleSubmit = async () => {
    if (!content.trim() || submitting) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/showcase/items/${itemId}/comments`, { content });
      setComments(prev => [...prev, r.data.comment]);
      setContent('');
    } catch (e) {
      alert(e.response?.data?.error || '发表失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-center text-gray-500 py-4 text-sm">加载留言...</div>;

  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-300 mb-3">
        留言 ({comments.length})
      </h4>

      <div className="space-y-3 max-h-60 overflow-y-auto pr-1 mb-4">
        {comments.length === 0 && (
          <p className="text-gray-500 text-sm text-center py-4">还没有留言，来说点什么吧</p>
        )}
        {comments.map(c => (
          <div key={c.id}>
            <div className={`rounded-lg p-3 text-sm ${c.is_tenant_reply ? 'bg-blue-900/30 border border-blue-500/20' : 'bg-gray-700/40'}`}>
              <div className="flex justify-between items-center mb-1">
                <span className={`font-medium text-xs ${c.is_tenant_reply ? 'text-blue-400' : 'text-gray-300'}`}>
                  {c.is_tenant_reply ? '🏷 作者回复' : c.user_name}
                </span>
                <span className="text-gray-600 text-xs">
                  {new Date(c.created_at).toLocaleDateString()}
                </span>
              </div>
              <p className="text-gray-200">{c.content}</p>
            </div>
            {c.replies?.map(r => (
              <div key={r.id} className="ml-4 mt-1 bg-blue-900/20 border border-blue-500/10 rounded-lg p-2.5 text-sm">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-blue-400 text-xs font-medium">
                    {r.is_tenant_reply ? '🏷 作者' : r.user_name}
                    {r.reply_to_user && <span className="text-gray-500 ml-1">→ {r.reply_to_user}</span>}
                  </span>
                  <span className="text-gray-600 text-xs">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
                <p className="text-gray-200">{r.content}</p>
              </div>
            ))}
          </div>
        ))}
      </div>

      {isLoggedIn ? (
        <div className="flex gap-2">
          <input
            value={content}
            onChange={e => setContent(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSubmit()}
            placeholder="写下你的留言..."
            maxLength={500}
            className="flex-1 bg-gray-700 text-white text-sm rounded-lg px-3 py-2 border border-gray-600 focus:outline-none focus:border-blue-500 placeholder-gray-500"
          />
          <button
            onClick={handleSubmit}
            disabled={submitting || !content.trim()}
            className="p-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded-lg transition-colors"
          >
            <PaperAirplaneIcon className="w-4 h-4 text-white" />
          </button>
        </div>
      ) : (
        <p className="text-center text-gray-500 text-sm py-2">
          <a href="/login" className="text-blue-400 hover:underline">登录</a> 后发表留言
        </p>
      )}
    </div>
  );
}

// ─── 私信弹窗 ─────────────────────────────────────────────────────────────────
function MessageModal({ item, onClose }) {
  const [content, setContent] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!content.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/showcase/items/${item.id}/messages`, { content });
      setSent(true);
    } catch (e) {
      alert(e.response?.data?.error || '发送失败');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="bg-gray-800 rounded-2xl p-6 w-full max-w-md border border-gray-600 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-white font-semibold">私信作者</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <p className="text-gray-400 text-sm mb-4">向「{item.project_name}」的作者发送私信</p>
        {sent ? (
          <div className="text-center py-6">
            <div className="text-green-400 text-4xl mb-2">✓</div>
            <p className="text-gray-300">私信已发送，可在个人中心查看回复</p>
          </div>
        ) : (
          <>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="写下你想说的话..."
              maxLength={1000}
              rows={4}
              className="w-full bg-gray-700 text-white text-sm rounded-lg px-3 py-2 border border-gray-600 focus:outline-none focus:border-blue-500 placeholder-gray-500 resize-none"
            />
            <div className="flex justify-end mt-3">
              <button
                onClick={handleSend}
                disabled={sending || !content.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-sm rounded-lg transition-colors"
              >
                {sending ? '发送中...' : '发送'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── 详情弹窗 ─────────────────────────────────────────────────────────────────
function DetailModal({ item: initItem, onClose, onStatsChange }) {
  const [item, setItem] = useState(initItem);
  const [tab, setTab] = useState('comments'); // 'comments' | 'message'
  const [showMsg, setShowMsg] = useState(false);
  const isLoggedIn = !!localStorage.getItem('user_token');

  const toggleLike = async () => {
    if (!isLoggedIn) return (window.location.href = '/login');
    try {
      const method = item.is_liked ? 'delete' : 'post';
      const r = await api[method](`/showcase/items/${item.id}/like`);
      const updated = { ...item, is_liked: r.data.liked, total_likes: r.data.total_likes };
      setItem(updated);
      onStatsChange(updated);
    } catch {}
  };

  const toggleFav = async () => {
    if (!isLoggedIn) return (window.location.href = '/login');
    try {
      const method = item.is_favorited ? 'delete' : 'post';
      const r = await api[method](`/showcase/items/${item.id}/favorite`);
      const updated = { ...item, is_favorited: r.data.favorited, total_favorites: r.data.total_favorites };
      setItem(updated);
      onStatsChange(updated);
    } catch {}
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-gray-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-600 shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* 封面 */}
        {item.cover_image && (
          <div className="relative h-48 overflow-hidden rounded-t-2xl">
            <img src={item.cover_image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-gray-800/80 to-transparent" />
          </div>
        )}

        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h2 className="text-xl font-bold text-white">{item.project_name}</h2>
              {item.is_pinned && <span className="text-xs text-yellow-400">📌 精选推荐</span>}
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white ml-4 shrink-0">
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <p className="text-gray-300 text-sm mb-4 leading-relaxed">{item.project_desc || '暂无描述'}</p>

          {/* 操作栏 */}
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <a
              href={`${import.meta.env.VITE_RUNTIME_BASE_URL || "https://www.noconb.com/run"}${item.project_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg transition-colors"
            >
              <EyeIcon className="w-4 h-4" /> 查看项目
            </a>
            <button
              onClick={toggleLike}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${item.is_liked ? 'bg-red-500/20 text-red-400' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
            >
              {item.is_liked ? <HeartSolid className="w-4 h-4" /> : <HeartIcon className="w-4 h-4" />}
              {item.total_likes}
            </button>
            <button
              onClick={toggleFav}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${item.is_favorited ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
            >
              {item.is_favorited ? <BookmarkSolid className="w-4 h-4" /> : <BookmarkIcon className="w-4 h-4" />}
              {item.total_favorites}
            </button>
            {isLoggedIn && (
              <button
                onClick={() => setShowMsg(true)}
                className="flex items-center gap-1.5 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm rounded-lg transition-colors"
              >
                <ChatBubbleLeftEllipsisIcon className="w-4 h-4" /> 私信作者
              </button>
            )}
          </div>

          {/* 留言区 */}
          <CommentSection itemId={item.id} />
        </div>
      </div>

      {showMsg && <MessageModal item={item} onClose={() => setShowMsg(false)} />}
    </div>
  );
}

// ─── 成果卡片 ─────────────────────────────────────────────────────────────────
function ShowcaseCard({ item, onClick, onLike, onFav }) {
  const isLoggedIn = !!localStorage.getItem('user_token');

  const handleLike = async (e) => {
    e.stopPropagation();
    if (!isLoggedIn) return (window.location.href = '/login');
    try {
      const method = item.is_liked ? 'delete' : 'post';
      const r = await api[method](`/showcase/items/${item.id}/like`);
      onLike(item.id, r.data.liked, r.data.total_likes);
    } catch {}
  };

  const handleFav = async (e) => {
    e.stopPropagation();
    if (!isLoggedIn) return (window.location.href = '/login');
    try {
      const method = item.is_favorited ? 'delete' : 'post';
      const r = await api[method](`/showcase/items/${item.id}/favorite`);
      onFav(item.id, r.data.favorited, r.data.total_favorites);
    } catch {}
  };

  return (
    <div
      onClick={onClick}
      className="bg-gray-800/60 rounded-2xl border border-gray-700 hover:border-gray-500 overflow-hidden cursor-pointer transition-all hover:shadow-xl hover:-translate-y-0.5 group"
    >
      {/* 封面 */}
      <div className="relative h-40 bg-gradient-to-br from-blue-900/30 to-purple-900/30 overflow-hidden">
        {item.cover_image ? (
          <img src={item.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <StarIcon className="w-12 h-12 text-gray-600" />
          </div>
        )}
        {item.is_pinned && (
          <span className="absolute top-2 left-2 bg-yellow-500/90 text-black text-xs font-bold px-2 py-0.5 rounded-full">📌 精选</span>
        )}
      </div>

      {/* 内容 */}
      <div className="p-4">
        <h3 className="text-white font-semibold text-sm mb-1 truncate">{item.project_name}</h3>
        <p className="text-gray-400 text-xs line-clamp-2 h-8 mb-3">{item.project_desc || '暂无描述'}</p>

        {/* 底部操作 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-0.5">
              <ChatBubbleLeftEllipsisIcon className="w-3.5 h-3.5" />{item.total_comments}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleLike}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${item.is_liked ? 'text-red-400 bg-red-500/10' : 'text-gray-400 hover:text-red-400 hover:bg-red-500/10'}`}
            >
              {item.is_liked ? <HeartSolid className="w-3.5 h-3.5" /> : <HeartIcon className="w-3.5 h-3.5" />}
              {item.total_likes}
            </button>
            <button
              onClick={handleFav}
              className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs transition-colors ${item.is_favorited ? 'text-yellow-400 bg-yellow-500/10' : 'text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/10'}`}
            >
              {item.is_favorited ? <BookmarkSolid className="w-3.5 h-3.5" /> : <BookmarkIcon className="w-3.5 h-3.5" />}
              {item.total_favorites}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 主页面 ───────────────────────────────────────────────────────────────────
export default function ShowcasePage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const pageSize = 12;

  const fetchItems = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const r = await api.get(`/showcase/items?page=${p}&pageSize=${pageSize}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setPage(p);
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(1); }, [fetchItems]);

  const handleLike = (id, liked, total_likes) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, is_liked: liked, total_likes } : i));
  };

  const handleFav = (id, favorited, total_favorites) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, is_favorited: favorited, total_favorites } : i));
  };

  const handleStatsChange = (updated) => {
    setItems(prev => prev.map(i => i.id === updated.id ? { ...i, ...updated } : i));
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="min-h-screen pt-20 pb-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* 头部 */}
        <div className="text-center py-12">
          <h1 className="text-4xl font-bold text-white mb-3">成果广场</h1>
          <p className="text-gray-400 text-lg">发现优秀的 NCB 作品，获取灵感</p>
        </div>

        {/* 列表 */}
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24">
            <StarIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">还没有成果展示</p>
            <p className="text-gray-500 text-sm mt-1">快去创建你的第一个项目吧</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {items.map(item => (
                <ShowcaseCard
                  key={item.id}
                  item={item}
                  onClick={() => setSelected(item)}
                  onLike={handleLike}
                  onFav={handleFav}
                />
              ))}
            </div>

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-10">
                <button
                  onClick={() => fetchItems(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white rounded-lg text-sm transition-colors"
                >
                  上一页
                </button>
                <span className="text-gray-400 text-sm px-3">{page} / {totalPages}</span>
                <button
                  onClick={() => fetchItems(page + 1)}
                  disabled={page === totalPages}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white rounded-lg text-sm transition-colors"
                >
                  下一页
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 详情弹窗 */}
      {selected && (
        <DetailModal
          item={selected}
          onClose={() => setSelected(null)}
          onStatsChange={handleStatsChange}
        />
      )}
    </div>
  );
}
